import React from "react";
import { Phone as PhoneIcon } from "lucide-react";

/**
 * PhoneInput — Ghana phone input using the LOCAL 0XXXXXXXXX format (no country code).
 * Accepts legacy inputs and normalises them:
 *   - `+233XXXXXXXXX` → `0XXXXXXXXX`
 *   - `233XXXXXXXXX`  → `0XXXXXXXXX`
 *   - `XXXXXXXXX` (9 digits) → `0XXXXXXXXX`
 *   - `0XXXXXXXXX` → as-is
 * Backend has the same normaliser (see normalize_phone in server.py).
 */
export function normalizePhone(raw) {
  if (!raw) return "";
  const cleaned = raw.replace(/[^\d+]/g, "");
  if (cleaned.startsWith("+233")) return "0" + cleaned.slice(4);
  if (cleaned.startsWith("233") && cleaned.length >= 12) return "0" + cleaned.slice(3);
  if (cleaned.startsWith("0")) return cleaned;
  if (/^\d{9}$/.test(cleaned)) return "0" + cleaned;
  return cleaned;
}

/** Format `0256309884` → `025 630 9884` for human display. */
function pretty(local) {
  if (!local || !local.startsWith("0")) return local || "";
  const d = local;
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 10)}`;
}

export default function PhoneInput({
  value, onChange, placeholder = "025 630 9884", required = false,
  testId = "phone-input", className = "", autoComplete = "tel",
}) {
  const display = /^0\d+$/.test(value || "") ? pretty(value) : value || "";

  const handleChange = (raw) => {
    const cleaned = raw.replace(/[^\d+]/g, "");
    if (cleaned.length === 0) { onChange(""); return; }
    onChange(normalizePhone(cleaned));
  };

  return (
    <div className={`relative ${className}`}>
      <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3D4] z-10" size={16} />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] tracking-[0.2em] uppercase font-bold text-[#A78BFA] pointer-events-none">GH 🇬🇭</span>
      <input
        type="tel"
        inputMode="tel"
        data-testid={testId}
        value={display}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        autoComplete={autoComplete}
        maxLength={14}
        className="w-full bg-[#09090F] border border-[#26263A] focus:border-[#7C3AED] rounded-xl pl-9 pr-14 py-3 text-sm outline-none"
      />
    </div>
  );
}
